import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCrsfToken } from '~/hooks/useCrsfToken'
import { LoginBadRequest, LoginCredentials, LoginResult } from '../types'

export const useLogin = () => {
    const queryClient = useQueryClient()
    const csrfToken = useCrsfToken()

    return useMutation<LoginResult, LoginBadRequest, unknown>({
        mutationFn: async ({ username, password }: LoginCredentials) => {
            const form = new FormData()
            form.append('username', username)
            form.append('password', password)

            const resp = await fetch('/api/account/login', {
                method: 'POST',
                headers: {
                    'X-CSRFToken': csrfToken,
                },
                body: form,
            })
            if (resp.status >= 500) {
                try {
                    return await resp.json()
                } catch (e) {
                    throw { __all__: [{ message: 'Internal server error', code: 'server_error' }] }
                }
            }
            if (resp.status === 403) {
                throw await resp.json()
            }
            // The password was correct, but a staff account with an enrolled
            // device owes a second factor before it gets a session. Returned as
            // a result rather than thrown: nothing failed, the login is simply
            // not finished yet.
            if (resp.status === 401) {
                const body = await resp.json().catch(() => ({}))
                if (body?.mfa_required) return { mfaRequired: true }
                throw {
                    __all__: [{ message: 'Could not sign you in. Please try again.', code: 'unauthorized' }],
                }
            }
            return { mfaRequired: false }
        },
        onSuccess: (result) => {
            // Skip while a challenge is pending: the viewer is still anonymous,
            // and refetching would bounce the user straight back to the login form.
            if (result?.mfaRequired) return
            return queryClient.invalidateQueries(['viewer'])
        },
    })
}
